import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Chip,
  Dialog,
  DialogContent,
  Tabs,
  Tab,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import EventNoteIcon from '@mui/icons-material/EventNote';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import HistoryIcon from '@mui/icons-material/History';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import AddTaskIcon from '@mui/icons-material/AddTask';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import api from '../services/api';
import ActivityForm from './ActivityForm';
import { useAuth } from '../contexts/AuthContext';
import TuneIcon from '@mui/icons-material/Tune';

interface Task {
  id: number;
  activity_type: string;
  title: string;
  description?: string;
  status: string;
  status_display: string;
  priority: string;
  priority_display: string;
  scheduled_at?: string;
  completed_at?: string;
  assigned_to_name?: string;
  created_at: string;
}

interface Stage {
  id: number;
  name: string;
  color: string;
  display_order: number;
}

interface LeadTag {
  id: number;
  name: string;
  color: string;
}

interface Opportunity {
  id: number;
  name?: string;
  internal_code: string;
  total_value: number;
  discount: number;
  addition: number;
  freight: number;
  final_value: number;
  status: string;
  loss_reason?: string;
  notes?: string;
  lead: number;
  lead_name: string;
  lead_email: string;
  lead_phone: string;
  lead_company: string;
  lead_tags?: LeadTag[];
  pipeline: { id: number; name: string };
  pipeline_name: string;
  stage: { id: number; name: string };
  stage_name: string;
  stage_color: string;
  attendant?: { id: number; name: string; email: string };
  status_display: string;
  created_at: string;
  last_moved_at: string;
  products?: any[];
}

interface DealDetailModalProps {
  open: boolean;
  opportunity: Opportunity | null;
  stages: Stage[];
  onClose: () => void;
  onUpdate: () => void;
  onDelete: (id: number) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ py: 2 }}>
    {value === index && children}
  </Box>
);

const DealDetailModal: React.FC<DealDetailModalProps> = ({
  open,
  opportunity: initialOpportunity,
  stages,
  onClose,
  onUpdate,
  onDelete
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [isEditingLead, setIsEditingLead] = useState(false);
  const [isEditingDeal, setIsEditingDeal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activities, setActivities] = useState<any[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<any>(undefined);
  const [savingTask, setSavingTask] = useState(false);
  const [companyUsers, setCompanyUsers] = useState<any[]>([]);

  // Local opportunity state for real-time updates
  const [opportunity, setOpportunity] = useState<Opportunity | null>(initialOpportunity);

  // Form states
  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });

  const [dealForm, setDealForm] = useState({
    total_value: 0,
    discount: 0,
    addition: 0,
    freight: 0,
    notes: ''
  });

  const [lossReason, setLossReason] = useState('');
  const [showLossModal, setShowLossModal] = useState(false);
  const [changingOwner, setChangingOwner] = useState(false);

  // Custom fields
  const [dealCustomFields, setDealCustomFields] = useState<any[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});
  const [savingCustomFields, setSavingCustomFields] = useState(false);

  // Buscar usuários da empresa para o dropdown de responsável
  useEffect(() => {
    const fetchCompanyUsers = async () => {
      try {
        const response = await api.get('/auth/users/company_users/');
        const data = response.data;
        const usersData = Array.isArray(data) ? data : (data.results || []);
        const mappedUsers = usersData.map((u: any) => ({
          id: u.id,
          email: u.email,
          get_full_name: u.get_full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
          role: u.role
        }));
        setCompanyUsers(mappedUsers);
      } catch (error) {
        console.error('Erro ao carregar usuários:', error);
        setCompanyUsers([]);
      }
    };
    fetchCompanyUsers();
  }, []);

  // Sync local state when prop changes (e.g., when opening modal with different opportunity)
  useEffect(() => {
    setOpportunity(initialOpportunity);
  }, [initialOpportunity]);

  // Initialize forms when opportunity changes
  useEffect(() => {
    if (initialOpportunity) {
      setLeadForm({
        name: initialOpportunity.lead_name || '',
        email: initialOpportunity.lead_email || '',
        phone: initialOpportunity.lead_phone || '',
        company: initialOpportunity.lead_company || ''
      });
      setDealForm({
        total_value: initialOpportunity.total_value || 0,
        discount: initialOpportunity.discount || 0,
        addition: initialOpportunity.addition || 0,
        freight: initialOpportunity.freight || 0,
        notes: initialOpportunity.notes || ''
      });
      setActiveTab(0);
      setIsEditingLead(false);
      setIsEditingDeal(false);
      setCustomFieldValues((initialOpportunity as any).custom_fields || {});
    }
  }, [initialOpportunity]);

  // Load activities when tab changes
  useEffect(() => {
    if (activeTab === 1 && opportunity) {
      loadActivities();
    }
    if (activeTab === 3 && opportunity) {
      loadTasks();
    }
    if (activeTab === 4 && dealCustomFields.length === 0) {
      loadDealCustomFields();
    }
  }, [activeTab, opportunity]);

  const loadDealCustomFields = async () => {
    try {
      const response = await api.get('/leads/custom-fields/?entity_type=deal');
      const fields = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setDealCustomFields(fields);
    } catch (error) {
      console.error('Error loading deal custom fields:', error);
    }
  };

  const handleSaveCustomFields = async () => {
    if (!opportunity) return;
    setSavingCustomFields(true);
    try {
      await api.patch(`/deals/deals/${opportunity.id}/`, { custom_fields: customFieldValues });
      onUpdate();
    } catch (error) {
      console.error('Error saving custom fields:', error);
    } finally {
      setSavingCustomFields(false);
    }
  };

  const loadActivities = async () => {
    if (!opportunity) return;
    setLoadingActivities(true);
    try {
      // Load deal history/activities
      const response = await api.get(`/deals/deals/${opportunity.id}/history/`);
      setActivities(response.data || []);
    } catch (error) {
      console.error('Error loading activities:', error);
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const loadTasks = async () => {
    if (!opportunity) return;
    setLoadingTasks(true);
    try {
      const response = await api.get(`/activities/activities/by_deal/?deal_id=${opportunity.id}`);
      setTasks(response.data || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
      setTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleSaveTask = async (data: any) => {
    if (!opportunity) return;
    setSavingTask(true);
    try {
      await api.post('/activities/activities/', {
        ...data,
        deal: opportunity.id,
        lead: opportunity.lead
      });
      setTaskModalOpen(false);
      setEditingTask(undefined);
      loadTasks();
    } catch (error) {
      console.error('Error saving task:', error);
      alert('Erro ao salvar tarefa');
    } finally {
      setSavingTask(false);
    }
  };

  const handleToggleTaskStatus = async (task: Task) => {
    try {
      const newStatus = task.status === 'completed' ? 'scheduled' : 'completed';
      if (newStatus === 'completed') {
        await api.post(`/activities/activities/${task.id}/complete/`);
      } else {
        await api.patch(`/activities/activities/${task.id}/`, { status: 'scheduled', completed_at: null });
      }
      loadTasks();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    try {
      await api.delete(`/activities/activities/${taskId}/`);
      loadTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return '#ef4444';
      case 'high': return '#f97316';
      case 'medium': return '#eab308';
      case 'low': return '#22c55e';
      default: return '#6b7280';
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value || 0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ganho': return '#22c55e';
      case 'perdido': return '#ef4444';
      default: return '#6366f1';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ganho': return 'Ganho';
      case 'perdido': return 'Perdido';
      default: return 'Em Andamento';
    }
  };

  const getInitials = (name: string) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (name: string) => {
    const colors = [
      '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e',
      '#f97316', '#eab308', '#22c55e', '#14b8a6',
      '#0ea5e9', '#6366f1'
    ];
    const hash = (name || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const getCurrentStageIndex = () => {
    if (!opportunity || !stages.length) return 0;
    const index = stages.findIndex(s => s.id === opportunity.stage.id);
    return index >= 0 ? index : 0;
  };

  const getProgressPercentage = () => {
    if (!stages.length) return 0;
    return ((getCurrentStageIndex() + 1) / stages.length) * 100;
  };

  const handleQuickContact = (type: string, value: string, leadId?: number) => {
    if (type === 'whatsapp' && leadId) {
      navigate(`/messaging?lead=${leadId}`);
      onClose();
    } else if (type === 'whatsapp' && value) {
      const phone = value.replace(/\D/g, '');
      window.open(`https://wa.me/${phone}`, '_blank');
    } else if (type === 'email' && value) {
      window.open(`mailto:${value}`, '_blank');
    } else if (type === 'phone' && value) {
      window.open(`tel:${value}`, '_blank');
    }
  };

  const handleSaveLead = async () => {
    if (!opportunity) return;
    setSaving(true);
    try {
      await api.patch(`/leads/leads/${opportunity.lead}/`, leadForm);
      setIsEditingLead(false);

      // Fetch updated opportunity data (includes lead data)
      const response = await api.get(`/deals/deals/${opportunity.id}/`);
      setOpportunity(response.data);

      onUpdate();
    } catch (error) {
      console.error('Error saving lead:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleChangeOwner = async (newOwnerId: number | null) => {
    if (!opportunity) return;
    setChangingOwner(true);
    try {
      await api.patch(`/deals/deals/${opportunity.id}/`, { owner: newOwnerId });

      // Fetch updated opportunity data
      const response = await api.get(`/deals/deals/${opportunity.id}/`);
      setOpportunity(response.data);

      onUpdate();
    } catch (error) {
      console.error('Error changing owner:', error);
    } finally {
      setChangingOwner(false);
    }
  };

  const handleSaveDeal = async () => {
    if (!opportunity) return;
    setSaving(true);
    try {
      await api.patch(`/deals/deals/${opportunity.id}/`, dealForm);
      setIsEditingDeal(false);

      // Fetch updated opportunity data
      const response = await api.get(`/deals/deals/${opportunity.id}/`);
      setOpportunity(response.data);

      onUpdate();
    } catch (error) {
      console.error('Error saving deal:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAsWon = async () => {
    if (!opportunity) return;
    setSaving(true);
    try {
      await api.post(`/deals/deals/${opportunity.id}/mark_won/`);

      // Fetch updated opportunity data
      const response = await api.get(`/deals/deals/${opportunity.id}/`);
      setOpportunity(response.data);

      onUpdate();
    } catch (error) {
      console.error('Error marking as won:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAsLost = async () => {
    if (!opportunity || !lossReason.trim()) return;
    setSaving(true);
    try {
      await api.post(`/deals/deals/${opportunity.id}/mark_lost/`, {
        loss_reason: lossReason
      });
      setShowLossModal(false);
      setLossReason('');

      // Fetch updated opportunity data
      const response = await api.get(`/deals/deals/${opportunity.id}/`);
      setOpportunity(response.data);

      onUpdate();
    } catch (error) {
      console.error('Error marking as lost:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleChangeStage = async (stageId: number) => {
    if (!opportunity || stageId === opportunity.stage.id) return;
    setSaving(true);
    try {
      await api.post(`/deals/deals/${opportunity.id}/change-stage/`, { stage_id: stageId });

      // Fetch updated opportunity data
      const response = await api.get(`/deals/deals/${opportunity.id}/`);
      setOpportunity(response.data);

      // Also notify parent to refresh the Kanban board
      onUpdate();
    } catch (error) {
      console.error('Error changing stage:', error);
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (!opportunity) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            maxHeight: '90vh'
          }
        }}
      >
        {/* Header */}
        <Box sx={{
          p: 3,
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          background: 'linear-gradient(135deg, rgba(99,102,241,0.05), rgba(139,92,246,0.03))'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            {/* Left: Lead info */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  fontSize: 20,
                  fontWeight: 700,
                  background: getAvatarColor(opportunity.lead_name || 'Lead')
                }}
              >
                {getInitials(opportunity.lead_name || 'Lead')}
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937' }}>
                  {opportunity.lead_name || 'Lead não encontrado'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                  {opportunity.lead_company && (
                    <Typography variant="body2" sx={{ color: '#6b7280', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <BusinessIcon sx={{ fontSize: 16 }} />
                      {opportunity.lead_company}
                    </Typography>
                  )}
                  {/* Só mostra tag se for ganho ou perdido */}
                  {opportunity.status !== 'open' && (
                    <Chip
                      label={getStatusLabel(opportunity.status)}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        background: getStatusColor(opportunity.status),
                        color: '#fff'
                      }}
                    />
                  )}
                </Box>
                {/* Lead Tags */}
                {opportunity.lead_tags && opportunity.lead_tags.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {opportunity.lead_tags.map((tag) => (
                      <Chip
                        key={tag.id}
                        label={tag.name}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: 11,
                          fontWeight: 500,
                          background: `${tag.color}20`,
                          color: tag.color,
                          border: `1px solid ${tag.color}40`,
                          '& .MuiChip-label': { px: 0.75 }
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Right: Value and close */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#1f2937' }}>
                  {formatCurrency(opportunity.final_value)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                  Valor da negociação
                </Typography>
              </Box>
              <IconButton onClick={onClose} sx={{ mt: -1 }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Pipeline Progress Bar - Interactive */}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                {opportunity.pipeline_name}
              </Typography>
              <Typography variant="caption" sx={{ color: '#6b7280' }}>
                Clique em um estágio para mover
              </Typography>
            </Box>

            {/* Interactive Stage Steps */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              position: 'relative'
            }}>
              {stages.map((stage, index) => {
                const isCurrent = stage.id === opportunity.stage.id;
                const isPast = index < getCurrentStageIndex();
                const isFirst = index === 0;
                const isLast = index === stages.length - 1;

                return (
                  <Tooltip
                    key={stage.id}
                    title={`Mover para: ${stage.name}`}
                    arrow
                    placement="top"
                  >
                    <Box
                      onClick={() => !saving && handleChangeStage(stage.id)}
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: saving ? 'wait' : 'pointer',
                        transition: 'all 0.2s ease',
                        opacity: saving ? 0.6 : 1,
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          '& .stage-bar': {
                            filter: 'brightness(1.1)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                          },
                          '& .stage-label': {
                            color: '#1f2937'
                          }
                        }
                      }}
                    >
                      {/* Stage Bar */}
                      <Box
                        className="stage-bar"
                        sx={{
                          width: '100%',
                          height: isCurrent ? 12 : 8,
                          borderRadius: isFirst ? '6px 0 0 6px' : isLast ? '0 6px 6px 0' : 0,
                          background: isCurrent
                            ? `linear-gradient(135deg, ${stage.color}, ${stage.color}dd)`
                            : isPast
                              ? stage.color
                              : 'rgba(0,0,0,0.1)',
                          transition: 'all 0.2s ease',
                          position: 'relative',
                          boxShadow: isCurrent ? `0 4px 12px ${stage.color}40` : 'none',
                          '&::after': isCurrent ? {
                            content: '""',
                            position: 'absolute',
                            top: -4,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 0,
                            height: 0,
                            borderLeft: '6px solid transparent',
                            borderRight: '6px solid transparent',
                            borderBottom: `6px solid ${stage.color}`
                          } : {}
                        }}
                      />

                      {/* Stage Name */}
                      <Typography
                        className="stage-label"
                        sx={{
                          fontSize: 10,
                          fontWeight: isCurrent ? 700 : 500,
                          color: isCurrent ? stage.color : isPast ? '#6b7280' : '#9ca3af',
                          mt: 1,
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '100%',
                          transition: 'color 0.2s ease'
                        }}
                      >
                        {stage.name}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>

            {/* Current Stage Indicator */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              mt: 2,
              py: 1,
              px: 2,
              background: `${stages[getCurrentStageIndex()]?.color}15`,
              borderRadius: 2,
              border: `1px solid ${stages[getCurrentStageIndex()]?.color}30`
            }}>
              <Box sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: stages[getCurrentStageIndex()]?.color
              }} />
              <Typography sx={{
                fontSize: 13,
                fontWeight: 600,
                color: stages[getCurrentStageIndex()]?.color
              }}>
                {opportunity.stage_name}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
                • Estágio {getCurrentStageIndex() + 1} de {stages.length}
              </Typography>
            </Box>
          </Box>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', minHeight: 500 }}>
            {/* Main Content */}
            <Box sx={{ flex: 1, p: 3 }}>
              {/* Quick Contact Bar */}
              <Box sx={{
                display: 'flex',
                gap: 1,
                mb: 3,
                p: 2,
                background: 'rgba(99,102,241,0.05)',
                borderRadius: 2,
                border: '1px solid rgba(99,102,241,0.1)'
              }}>
                <Typography sx={{ fontSize: 13, color: '#6b7280', alignSelf: 'center', mr: 1 }}>
                  Contato rápido:
                </Typography>
                {opportunity.lead_phone && (
                  <>
                    <Tooltip title="Abrir conversa" arrow>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<WhatsAppIcon />}
                        onClick={() => handleQuickContact('whatsapp', opportunity.lead_phone, opportunity.lead)}
                        sx={{
                          borderColor: '#25d366',
                          color: '#25d366',
                          '&:hover': { background: '#25d366', borderColor: '#25d366', color: '#fff' }
                        }}
                      >
                        Conversa
                      </Button>
                    </Tooltip>
                    <Tooltip title="Ligar" arrow>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PhoneIcon />}
                        onClick={() => handleQuickContact('phone', opportunity.lead_phone)}
                        sx={{
                          borderColor: '#6366f1',
                          color: '#6366f1',
                          '&:hover': { background: '#6366f1', borderColor: '#6366f1', color: '#fff' }
                        }}
                      >
                        Ligar
                      </Button>
                    </Tooltip>
                  </>
                )}
                {opportunity.lead_email && (
                  <Tooltip title="Enviar email" arrow>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EmailIcon />}
                      onClick={() => handleQuickContact('email', opportunity.lead_email)}
                      sx={{
                        borderColor: '#6366f1',
                        color: '#6366f1',
                        '&:hover': { background: '#6366f1', borderColor: '#6366f1', color: '#fff' }
                      }}
                    >
                      Email
                    </Button>
                  </Tooltip>
                )}
              </Box>

              {/* Tabs */}
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{
                  borderBottom: '1px solid rgba(0,0,0,0.08)',
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    minWidth: 100
                  }
                }}
              >
                <Tab icon={<TrendingUpIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Resumo" />
                <Tab icon={<HistoryIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Histórico" />
                <Tab icon={<AttachMoneyIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Valores" />
                <Tab icon={<TaskAltIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Tarefas" />
                <Tab icon={<TuneIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Campos" />
              </Tabs>

              {/* Tab: Resumo */}
              <TabPanel value={activeTab} index={0}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                  {/* Dados do Lead */}
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ fontSize: 20, color: '#6366f1' }} />
                        Dados do Lead
                      </Typography>
                      {!isEditingLead ? (
                        <IconButton size="small" onClick={() => setIsEditingLead(true)}>
                          <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton size="small" onClick={handleSaveLead} disabled={saving} color="primary">
                            <SaveIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                          <IconButton size="small" onClick={() => setIsEditingLead(false)}>
                            <CancelIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{
                      p: 2,
                      background: 'rgba(0,0,0,0.02)',
                      borderRadius: 2,
                      border: '1px solid rgba(0,0,0,0.06)'
                    }}>
                      {/* Lead ID - não editável */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 2, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                        <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                          ID do Lead
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontWeight: 600, color: '#6b7280' }}>
                            #{opportunity.lead}
                          </Typography>
                          <IconButton size="small" onClick={() => copyToClipboard(String(opportunity.lead))}>
                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      </Box>

                      {isEditingLead ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <TextField
                            label="Nome"
                            size="small"
                            fullWidth
                            value={leadForm.name}
                            onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                          />
                          <TextField
                            label="Email"
                            size="small"
                            fullWidth
                            value={leadForm.email}
                            onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                          />
                          <TextField
                            label="Telefone"
                            size="small"
                            fullWidth
                            value={leadForm.phone}
                            onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                          />
                          <TextField
                            label="Empresa"
                            size="small"
                            fullWidth
                            value={leadForm.company}
                            onChange={(e) => setLeadForm({ ...leadForm, company: e.target.value })}
                          />
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>Nome</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{leadForm.name || '-'}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>Telefone</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{leadForm.phone || '-'}</Typography>
                              {leadForm.phone && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleQuickContact('whatsapp', leadForm.phone, opportunity.lead)}
                                  sx={{ color: '#25d366' }}
                                >
                                  <WhatsAppIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              )}
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>Email</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{leadForm.email || '-'}</Typography>
                              {leadForm.email && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleQuickContact('email', leadForm.email)}
                                  sx={{ color: '#6366f1' }}
                                >
                                  <EmailIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              )}
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>Empresa</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{leadForm.company || '-'}</Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Dados da Negociação */}
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingUpIcon sx={{ fontSize: 20, color: '#6366f1' }} />
                      Dados da Negociação
                    </Typography>

                    <Box sx={{
                      p: 2,
                      background: 'rgba(0,0,0,0.02)',
                      borderRadius: 2,
                      border: '1px solid rgba(0,0,0,0.06)'
                    }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>Pipeline</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{opportunity.pipeline_name}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>Estágio</Typography>
                          <Chip
                            label={opportunity.stage_name}
                            size="small"
                            sx={{
                              height: 22,
                              background: opportunity.stage_color,
                              color: '#fff',
                              fontWeight: 600
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>Responsável</Typography>
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                              value={opportunity.attendant?.id || ''}
                              onChange={(e) => handleChangeOwner(e.target.value ? Number(e.target.value) : null)}
                              disabled={changingOwner}
                              displayEmpty
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                '& .MuiSelect-select': {
                                  py: 0.5,
                                  pr: 3
                                }
                              }}
                            >
                              <MenuItem value="">
                                <em>Sem responsável</em>
                              </MenuItem>
                              {companyUsers.map((u) => (
                                <MenuItem key={u.id} value={u.id}>
                                  {u.get_full_name || u.email}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>Criado em</Typography>
                          <Typography variant="body2">{formatDateShort(opportunity.created_at)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>Última movimentação</Typography>
                          <Typography variant="body2">{formatDateShort(opportunity.last_moved_at)}</Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Loss reason if applicable */}
                    {opportunity.status === 'perdido' && opportunity.loss_reason && (
                      <Box sx={{
                        mt: 2,
                        p: 2,
                        background: 'rgba(239,68,68,0.08)',
                        border: '1px solid rgba(239,68,68,0.2)',
                        borderRadius: 2
                      }}>
                        <Typography variant="subtitle2" sx={{ color: '#dc2626', fontWeight: 700, mb: 1 }}>
                          Motivo da Perda
                        </Typography>
                        <Typography variant="body2">{opportunity.loss_reason}</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Notas */}
                {opportunity.notes && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                      Notas
                    </Typography>
                    <Box sx={{
                      p: 2,
                      background: 'rgba(0,0,0,0.02)',
                      borderRadius: 2,
                      whiteSpace: 'pre-wrap'
                    }}>
                      {opportunity.notes}
                    </Box>
                  </Box>
                )}
              </TabPanel>

              {/* Tab: Atividades */}
              <TabPanel value={activeTab} index={1}>
                {loadingActivities ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : activities.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4, color: '#9ca3af' }}>
                    <HistoryIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                    <Typography>Nenhuma atividade registrada</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {activities.map((activity, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          gap: 2,
                          p: 2,
                          background: 'rgba(0,0,0,0.02)',
                          borderRadius: 2,
                          borderLeft: '3px solid #6366f1'
                        }}
                      >
                        <Box sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          background: 'rgba(99,102,241,0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <SwapHorizIcon sx={{ fontSize: 18, color: '#6366f1' }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {activity.description || activity.action}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                            {formatDate(activity.created_at || activity.timestamp)}
                            {activity.user_name && ` • ${activity.user_name}`}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </TabPanel>

              {/* Tab: Valores */}
              <TabPanel value={activeTab} index={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Detalhamento de Valores
                  </Typography>
                  {!isEditingDeal ? (
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => setIsEditingDeal(true)}
                    >
                      Editar
                    </Button>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveDeal}
                        disabled={saving}
                      >
                        Salvar
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setIsEditingDeal(false)}
                      >
                        Cancelar
                      </Button>
                    </Box>
                  )}
                </Box>

                <Box sx={{
                  p: 3,
                  background: 'rgba(0,0,0,0.02)',
                  borderRadius: 2,
                  border: '1px solid rgba(0,0,0,0.06)'
                }}>
                  {isEditingDeal ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        label="Valor Total"
                        type="number"
                        size="small"
                        fullWidth
                        value={dealForm.total_value}
                        onChange={(e) => setDealForm({ ...dealForm, total_value: parseFloat(e.target.value) || 0 })}
                      />
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <TextField
                          label="Desconto"
                          type="number"
                          size="small"
                          value={dealForm.discount}
                          onChange={(e) => setDealForm({ ...dealForm, discount: parseFloat(e.target.value) || 0 })}
                        />
                        <TextField
                          label="Acréscimo"
                          type="number"
                          size="small"
                          value={dealForm.addition}
                          onChange={(e) => setDealForm({ ...dealForm, addition: parseFloat(e.target.value) || 0 })}
                        />
                      </Box>
                      <TextField
                        label="Frete"
                        type="number"
                        size="small"
                        fullWidth
                        value={dealForm.freight}
                        onChange={(e) => setDealForm({ ...dealForm, freight: parseFloat(e.target.value) || 0 })}
                      />
                      <TextField
                        label="Notas"
                        multiline
                        rows={3}
                        size="small"
                        fullWidth
                        value={dealForm.notes}
                        onChange={(e) => setDealForm({ ...dealForm, notes: e.target.value })}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Valor Total</Typography>
                        <Typography sx={{ fontWeight: 600 }}>{formatCurrency(opportunity.total_value)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ color: '#ef4444' }}>Desconto</Typography>
                        <Typography sx={{ color: '#ef4444' }}>- {formatCurrency(opportunity.discount)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ color: '#22c55e' }}>Acréscimo</Typography>
                        <Typography sx={{ color: '#22c55e' }}>+ {formatCurrency(opportunity.addition)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Frete</Typography>
                        <Typography>{formatCurrency(opportunity.freight)}</Typography>
                      </Box>
                      <Divider />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Valor Final</Typography>
                        <Typography sx={{ fontWeight: 800, fontSize: 20, color: '#22c55e' }}>
                          {formatCurrency(opportunity.final_value)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Produtos */}
                {opportunity.products && opportunity.products.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                      Produtos ({opportunity.products.length})
                    </Typography>
                    <Box sx={{
                      border: '1px solid rgba(0,0,0,0.1)',
                      borderRadius: 2,
                      overflow: 'hidden'
                    }}>
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 1fr 1fr',
                        gap: 2,
                        p: 1.5,
                        background: 'rgba(0,0,0,0.04)',
                        fontWeight: 600,
                        fontSize: 13
                      }}>
                        <Box>Produto</Box>
                        <Box sx={{ textAlign: 'right' }}>Qtd</Box>
                        <Box sx={{ textAlign: 'right' }}>Preço</Box>
                        <Box sx={{ textAlign: 'right' }}>Total</Box>
                      </Box>
                      {opportunity.products.map((product: any) => (
                        <Box
                          key={product.id}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '2fr 1fr 1fr 1fr',
                            gap: 2,
                            p: 1.5,
                            borderTop: '1px solid rgba(0,0,0,0.06)',
                            fontSize: 14
                          }}
                        >
                          <Box>{product.name}</Box>
                          <Box sx={{ textAlign: 'right' }}>{product.quantity}</Box>
                          <Box sx={{ textAlign: 'right' }}>{formatCurrency(product.price)}</Box>
                          <Box sx={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(product.total_price)}</Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </TabPanel>

              {/* Tab: Tarefas */}
              <TabPanel value={activeTab} index={3}>
                {/* Header com botão adicionar */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Tarefas da Negociação
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddTaskIcon />}
                    onClick={() => setTaskModalOpen(true)}
                    sx={{ background: '#6366f1', '&:hover': { background: '#4f46e5' } }}
                  >
                    Nova Tarefa
                  </Button>
                </Box>

                {/* Lista de tarefas */}
                {loadingTasks ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : tasks.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4, color: '#9ca3af' }}>
                    <TaskAltIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                    <Typography>Nenhuma tarefa criada</Typography>
                    <Typography variant="caption">
                      Clique em "Nova Tarefa" para adicionar
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {tasks.map((task) => (
                      <Box
                        key={task.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 2,
                          p: 2,
                          background: task.status === 'completed' ? 'rgba(34,197,94,0.05)' : 'rgba(0,0,0,0.02)',
                          borderRadius: 2,
                          border: task.status === 'completed' ? '1px solid rgba(34,197,94,0.2)' : '1px solid rgba(0,0,0,0.06)',
                          transition: 'all 0.2s',
                          '&:hover': {
                            background: task.status === 'completed' ? 'rgba(34,197,94,0.08)' : 'rgba(0,0,0,0.04)',
                            '& .task-actions': { opacity: 1 }
                          }
                        }}
                      >
                        {/* Checkbox */}
                        <IconButton
                          size="small"
                          onClick={() => handleToggleTaskStatus(task)}
                          sx={{
                            mt: 0.5,
                            color: task.status === 'completed' ? '#22c55e' : '#9ca3af',
                            '&:hover': { color: '#22c55e' }
                          }}
                        >
                          {task.status === 'completed' ? (
                            <CheckCircleOutlineIcon />
                          ) : (
                            <RadioButtonUncheckedIcon />
                          )}
                        </IconButton>

                        {/* Conteúdo */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: 14,
                                textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                                color: task.status === 'completed' ? '#9ca3af' : '#1f2937'
                              }}
                            >
                              {task.title}
                            </Typography>
                            <Chip
                              label={task.priority_display}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: 10,
                                fontWeight: 600,
                                background: `${getPriorityColor(task.priority)}20`,
                                color: getPriorityColor(task.priority),
                                border: `1px solid ${getPriorityColor(task.priority)}40`
                              }}
                            />
                          </Box>
                          {task.description && (
                            <Typography variant="body2" sx={{ color: '#6b7280', mb: 0.5 }}>
                              {task.description}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {task.scheduled_at && (
                              <Typography variant="caption" sx={{ color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CalendarTodayIcon sx={{ fontSize: 12 }} />
                                {new Date(task.scheduled_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                            )}
                            {task.assigned_to_name && (
                              <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                                {task.assigned_to_name}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {/* Ações */}
                        <Box className="task-actions" sx={{ opacity: 0, transition: 'opacity 0.2s' }}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (window.confirm('Excluir esta tarefa?')) {
                                handleDeleteTask(task.id);
                              }
                            }}
                            sx={{ color: '#9ca3af', '&:hover': { color: '#ef4444' } }}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </TabPanel>

              {/* Tab: Campos Personalizados */}
              <TabPanel value={activeTab} index={4}>
                {dealCustomFields.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6, color: '#9ca3af' }}>
                    <TuneIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
                    <Typography sx={{ mb: 1 }}>Nenhum campo personalizado configurado</Typography>
                    <Typography variant="caption">
                      Acesse Configurações → Campos Customizados para criar campos para negociações
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 3 }}>
                      {dealCustomFields.map((field: any) => (
                        <Box key={field.id}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#6b7280', mb: 0.5, display: 'block' }}>
                            {field.label}{field.is_required && ' *'}
                          </Typography>

                          {field.field_type === 'select' ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={customFieldValues[field.name] || ''}
                                onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.name]: e.target.value }))}
                                displayEmpty
                                sx={{ background: 'rgba(255,255,255,0.8)' }}
                              >
                                <MenuItem value=""><em>Selecionar...</em></MenuItem>
                                {(field.options || []).map((opt: string) => (
                                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : field.field_type === 'multiselect' ? (
                            <FormControl fullWidth size="small">
                              <Select
                                multiple
                                value={Array.isArray(customFieldValues[field.name]) ? customFieldValues[field.name] : []}
                                onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.name]: e.target.value }))}
                                renderValue={(selected: any) => (selected as string[]).join(', ')}
                                sx={{ background: 'rgba(255,255,255,0.8)' }}
                              >
                                {(field.options || []).map((opt: string) => (
                                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : field.field_type === 'checkbox' ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <input
                                type="checkbox"
                                checked={!!customFieldValues[field.name]}
                                onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.name]: e.target.checked }))}
                                style={{ width: 18, height: 18, cursor: 'pointer' }}
                              />
                              <Typography variant="body2">{field.label}</Typography>
                            </Box>
                          ) : (
                            <TextField
                              fullWidth
                              size="small"
                              type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'}
                              value={customFieldValues[field.name] || ''}
                              onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.name]: e.target.value }))}
                              placeholder={field.default_value || ''}
                              InputLabelProps={field.field_type === 'date' ? { shrink: true } : undefined}
                              sx={{ '& .MuiOutlinedInput-root': { background: 'rgba(255,255,255,0.8)' } }}
                            />
                          )}

                          {field.description && (
                            <Typography variant="caption" sx={{ color: '#9ca3af', mt: 0.5, display: 'block' }}>
                              {field.description}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        onClick={handleSaveCustomFields}
                        disabled={savingCustomFields}
                        startIcon={savingCustomFields ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        sx={{ background: '#6366f1', '&:hover': { background: '#4f46e5' } }}
                      >
                        {savingCustomFields ? 'Salvando...' : 'Salvar Campos'}
                      </Button>
                    </Box>
                  </Box>
                )}
              </TabPanel>
            </Box>

            {/* Sidebar - Actions */}
            <Box sx={{
              width: 280,
              borderLeft: '1px solid rgba(0,0,0,0.08)',
              p: 3,
              background: 'rgba(0,0,0,0.02)'
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#6b7280' }}>
                Ações Rápidas
              </Typography>

              {opportunity.status === 'open' && (
                <>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleMarkAsWon}
                    disabled={saving}
                    sx={{
                      mb: 1.5,
                      background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                      '&:hover': { background: 'linear-gradient(135deg, #16a34a, #15803d)' }
                    }}
                  >
                    Marcar como Ganho
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<CancelOutlinedIcon />}
                    onClick={() => setShowLossModal(true)}
                    sx={{
                      mb: 3,
                      borderColor: '#ef4444',
                      color: '#ef4444',
                      '&:hover': { background: 'rgba(239,68,68,0.1)', borderColor: '#ef4444' }
                    }}
                  >
                    Marcar como Perdido
                  </Button>
                </>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* Change Stage */}
              <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 600, mb: 1, display: 'block' }}>
                Mover para estágio
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                <Select
                  value={opportunity.stage.id}
                  onChange={(e) => handleChangeStage(Number(e.target.value))}
                >
                  {stages.map(stage => (
                    <MenuItem key={stage.id} value={stage.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: stage.color }} />
                        {stage.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Divider sx={{ mb: 2 }} />

              {/* Info */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#6b7280' }}>
                  <CalendarTodayIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption">
                    Criado {formatDateShort(opportunity.created_at)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#6b7280' }}>
                  <SwapHorizIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption">
                    Movido {formatDateShort(opportunity.last_moved_at)}
                  </Typography>
                </Box>
              </Box>

              {/* Delete */}
              <Box sx={{ mt: 'auto', pt: 3 }}>
                <Button
                  fullWidth
                  variant="text"
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    if (window.confirm('Tem certeza que deseja excluir esta negociação?')) {
                      onDelete(opportunity.id);
                    }
                  }}
                  sx={{
                    color: '#9ca3af',
                    '&:hover': { color: '#ef4444', background: 'rgba(239,68,68,0.1)' }
                  }}
                >
                  Excluir negociação
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Loss Reason Modal */}
      <Dialog
        open={showLossModal}
        onClose={() => setShowLossModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Motivo da Perda
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Descreva o motivo da perda desta negociação..."
            value={lossReason}
            onChange={(e) => setLossReason(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={() => setShowLossModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleMarkAsLost}
              disabled={!lossReason.trim() || saving}
              sx={{
                background: '#ef4444',
                '&:hover': { background: '#dc2626' }
              }}
            >
              Confirmar Perda
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Activity Form Modal */}
      {opportunity && opportunity.lead && (
        <ActivityForm
          open={taskModalOpen}
          onClose={() => {
            setTaskModalOpen(false);
            setEditingTask(undefined);
          }}
          onSave={handleSaveTask}
          activity={editingTask}
          leads={[
            {
              id: opportunity.lead,
              name: opportunity.lead_name || 'Lead',
              email: opportunity.lead_email || undefined,
              phone: opportunity.lead_phone || undefined,
              company: opportunity.lead_company || undefined,
              status: 'active',
              source: undefined,
              notes: undefined,
              created_at: opportunity.created_at,
              updated_at: opportunity.last_moved_at
            }
          ]}
          deals={[
            {
              id: opportunity.id,
              name: opportunity.name || opportunity.pipeline_name,
              lead: opportunity.lead,
              status: opportunity.status
            }
          ]}
          users={companyUsers}
          isSubmitting={savingTask}
          defaultDealId={opportunity.id}
          defaultLeadId={opportunity.lead}
          currentUserId={user?.id}
        />
      )}
    </>
  );
};

export default DealDetailModal;
